/**
 * Directiva `v-drag-scroll`: desplazar un contenedor arrastrando con el ratón.
 *
 * El árbol del modelo desborda en ambos ejes y navegarlo solo con las barras de
 * desplazamiento resulta incómodo. Se implementa como directiva, y no como
 * componente envoltorio, porque es puro comportamiento de DOM y no debe imponer
 * ningún marcado.
 *
 * Registrada globalmente en `main.ts`.
 */

import type { Directive } from 'vue'

/**
 * Convierte el elemento en un panel arrastrable.
 *
 * Detalle que hace usable la directiva: el arrastre se IGNORA si empezó sobre un
 * control interactivo (`button`, `a`, `input`, `select`, `textarea`, `label`).
 * Sin esa comprobación, cualquier clic en un botón dentro del árbol iniciaría un
 * desplazamiento y el clic se perdería.
 *
 * Durante el arrastre se desactiva la selección de texto y el cursor pasa a
 * `grabbing`, para que el gesto se distinga de una selección.
 *
 * El elemento debe poder desplazarse (`overflow: auto`/`scroll`); sobre un
 * contenedor sin desbordamiento la directiva se monta pero no tiene efecto
 * visible.
 *
 * @example
 * <div v-drag-scroll class="lienzo-arbol">…</div>
 */
export const vDragScroll: Directive<HTMLElement> = {
  mounted(el) {
    let isDown = false
    let startX = 0
    let startY = 0
    let scrollLeft = 0
    let scrollTop = 0

    el.style.cursor = 'grab'

    const onMouseDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('button, a, input, select, textarea, label')) return
      isDown = true
      startX = e.clientX
      startY = e.clientY
      scrollLeft = el.scrollLeft
      scrollTop = el.scrollTop
      el.style.cursor = 'grabbing'
      el.style.userSelect = 'none'
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return
      e.preventDefault()
      el.scrollLeft = scrollLeft - (e.clientX - startX)
      el.scrollTop  = scrollTop  - (e.clientY - startY)
    }

    const onRelease = () => {
      if (!isDown) return
      isDown = false
      el.style.cursor = 'grab'
      el.style.userSelect = ''
    }

    el.addEventListener('mousedown', onMouseDown)
    el.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onRelease)

    const elAny = el as any
    elAny.__dragScrollCleanup = () => {
      el.removeEventListener('mousedown', onMouseDown)
      el.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onRelease)
    }
  },
  unmounted(el) {
    const elAny = el as any
    elAny.__dragScrollCleanup?.()
  }
}
